import { useEffect, useState } from 'react';

import { Add, Home, Pending } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Paper,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { getGuestWalletsSummaryInfo } from 'src/services/wallet';

import { CreateWallet } from './CreateWallet';
import { useMonitorContext } from './MonitorContextProvider';
import { GuestEnvironmentAlert } from './components/GuestEnvironmentAlert';
import { GuestAcceptedWalletsTab } from './components/Guests/GuestAcceptedWalletsTab';
import { GuestEnvironmentSelector } from './components/Guests/GuestEnvironmentSelector';
import { GuestPendingWalletTab } from './components/Guests/GuestPendingWalletTab';
import { WalletSummaryCard } from './components/WalletSummaryCard';

type GuestUserMonitorTabValue = 'accepted' | 'pending';

export default function GuestUserMonitor() {
  const { auth } = useAuthContext();
  const { setGuestEnvironmentOwnerId } = useMonitorContext();
  const [openCreateWallet, setOpenCreateWallet] = useState(false);
  const [summaryInfo, setSummaryInfo] = useState<{
    walletsCount: number;
    fundsCount: number;
  }>({
    walletsCount: 0,
    fundsCount: 0,
  });
  const { ownerId } = useParams<{ ownerId: string }>();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const getActiveTabFromUrl = (): GuestUserMonitorTabValue => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as GuestUserMonitorTabValue;
    return tab && ['accepted', 'pending'].includes(tab) ? tab : 'accepted';
  };

  const [activeTab, setActiveTab] = useState<GuestUserMonitorTabValue>(
    getActiveTabFromUrl(),
  );

  const guestUserWalletEnvironment =
    auth.user?.guestUserWalletEnvironments?.find(
      (environment) => environment.owner.id === Number(ownerId),
    );

  const {
    isLoading: isLoadingSummaryInfo,
    isFetching: isFetchingSummaryInfo,
    refetch: refetchSummaryInfo,
  } = useQuery({
    queryKey: ['guest-wallets-summary-info', ownerId, activeTab],
    queryFn: () =>
      getGuestWalletsSummaryInfo({
        ownerId: String(ownerId),
        status: activeTab,
      }),
    onSuccess: (data) => {
      setSummaryInfo(data);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const renderTabContent = () => {
    switch (activeTab) {
      default:
      case 'accepted':
        return (
          <GuestAcceptedWalletsTab
            ownerId={String(guestUserWalletEnvironment?.owner.id)}
          />
        );
      case 'pending':
        return (
          <GuestPendingWalletTab
            ownerId={String(guestUserWalletEnvironment?.owner.id)}
          />
        );
    }
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: GuestUserMonitorTabValue,
  ) => {
    setActiveTab(newValue);
    const params = new URLSearchParams(location.search);
    params.set('tab', newValue);
    history.push({ search: params.toString() });
  };

  /** Manipula abertura do modal de criação de carteiras pelo convidado.
   * Obs.: Convidados de ambiente não precisam validar limites de carteiras pois
   * a carteira será criada como draft e precisa de aprovação do owner. */
  const handleOpenCreateWallet = () => {
    setOpenCreateWallet(true);
  };

  const handleFinish = () => {
    setOpenCreateWallet(false);
    refetchSummaryInfo();
    queryClient.invalidateQueries(['accepted-wallets']);
    queryClient.invalidateQueries(['pending-wallets']);
    setActiveTab('pending');
  };

  useEffect(() => {
    if (ownerId && auth.user?.guestUserWalletEnvironments) {
      const guestUserWalletEnvironment =
        auth.user?.guestUserWalletEnvironments?.find(
          (environment) => environment.owner.id === Number(ownerId),
        );

      if (!guestUserWalletEnvironment) {
        history.replace('/monitor');
        auth.user?.guestUserWalletEnvironments?.length > 0 &&
          toast.error(
            'Ambiente não encontrado. Você foi redirecionado para o seu ambiente padrão.',
          );
      } else {
        setGuestEnvironmentOwnerId(ownerId);
      }
    }
  }, [ownerId, auth.user?.guestUserWalletEnvironments]);

  useEffect(() => {
    return () => {
      setGuestEnvironmentOwnerId(null);
    };
  }, []);

  return (
    <>
      <GuestEnvironmentSelector />
      <GuestEnvironmentAlert />
      <Box component="section">
        <Paper
          elevation={2}
          sx={{
            backgroundColor: 'rgba(252, 221, 172, 0.1)',
          }}>
          <Box
            p={1}
            borderRadius={'5px'}
            display="flex"
            flexDirection="column"
            gap={2}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={4}>
              <Typography variant="h5">Painel de Carteiras</Typography>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleOpenCreateWallet}>
                  Nova Carteira
                </Button>
              </Box>
            </Box>
            <Box
              width="100%"
              display="flex"
              flexDirection={{ xs: 'column', md: 'row' }}
              gap={2}
              px={3}
              pb={2}>
              <WalletSummaryCard
                data={{
                  title: 'Total de Carteiras',
                  description:
                    activeTab === 'accepted' ? 'aceitas' : 'pendentes',
                  value: summaryInfo.walletsCount,
                  isLoading: isLoadingSummaryInfo || isFetchingSummaryInfo,
                  color: '#fff',
                }}
              />
              <WalletSummaryCard
                data={{
                  title: 'Total de Fundos',
                  description: 'alocados',
                  value: summaryInfo.fundsCount,
                  isLoading: isLoadingSummaryInfo || isFetchingSummaryInfo,
                  color: '#fff',
                }}
              />
            </Box>

            <Box px={3}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap={1}
                mb={1}
                width="100%">
                <Typography variant="h6" fontWeight="bold">
                  Carteiras de {guestUserWalletEnvironment?.owner.name}
                </Typography>
                <Divider
                  orientation="horizontal"
                  sx={{ width: '100%', borderBottom: '2px solid #FF8211' }}
                />
              </Box>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant={isMobile ? 'fullWidth' : 'standard'}>
                <Tab
                  icon={<Home />}
                  label="Carteiras Ativas"
                  value="accepted"
                />
                <Tab
                  icon={<Pending />}
                  label="Carteiras Pendentes"
                  value="pending"
                />
              </Tabs>
            </Box>

            <Box>{renderTabContent()}</Box>
          </Box>
        </Paper>
      </Box>
      <CreateWallet
        open={openCreateWallet}
        onClose={() => setOpenCreateWallet(false)}
        onFinish={handleFinish}
      />
    </>
  );
}
